import { Product, ProductPrice, ProductPricePricePoint } from "@/apidtos";
import { useApi } from "@core/api";
import { PagedResultsTypeahead } from "@core/components/PagedResultsTypeahead";
import { LabeledInput } from "@core/components/forms/LabeledInput";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { TooltipWrapper } from "@core/components/TooltipWrapper";
import { Button } from "@core/components/ui/button";
import { Surface } from "@core/components/ui/surface";
import { H2 } from "@core/components/ui/typography";
import { faQuestionCircle, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useState } from "react";

interface AdminProductPricePricePointDropdownProps {
  price: ProductPrice;
  index: number;
  product: Product;
}
const AdminProductPricePricePointDropdown = (args: AdminProductPricePricePointDropdownProps) => {
  const api = useApi();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // We need a valid list of price points to render the dropdown.
  const [pricePointFilters, setPricePointFilters] = useState<{ Query?: string; PageIndex?: number; PageSize?: number } | null>({
    Query: "",
    PageIndex: 0,
    PageSize: 20,
  });

  const pricePointsQuery = useQuery({
    queryKey: [
      "pricepoints",
      pricePointFilters?.Query ?? "",
      pricePointFilters?.PageIndex ?? 0,
      pricePointFilters?.PageSize ?? 20,
    ],
    queryFn: async () => {
      if (!pricePointFilters) {
        return { Results: [], TotalCount: 0, TotalPages: 0 };
      }
      return api.ListPricePoint(pricePointFilters);
    },
    // @ts-expect-error this is fine
    keepPreviousData: true,
    enabled: !!pricePointFilters,
  });

  // Query for currently applied price points for the current price.
  const { data: productPricePricePoints, isLoading: isProductPricePointsLoading } = useQuery({
    queryKey: ["productPricePricePoints", args.price.Id],
    queryFn: async () => {
      return api.ListProductPricePricePoint({
        ProductPriceId: args.price.Id,
      });
    },
    // // @ts-expect-error it's just a default
    // initialData: { Results: [] },
  });

  // Mutations for adding/removing price points to/from the current price.
  const selectProductPricePricePoint = useMutation({
    mutationFn: async (pricePointId: number) => {
      // @ts-expect-error this is enough to create the record
      return api.CreateProductPricePricePoint({
        ProductPriceId: args.price.Id,
        PricePointId: pricePointId,
      });
    },
    onSuccess: () => {
      // Invalidate the query to refresh the list of price points for the current price.
      queryClient.invalidateQueries({
        queryKey: ["productPricePricePoints", args.price.Id],
      });
    }
  });
  const deleteProductPricePricePoint = useMutation({
    mutationFn: async (productPricePricePointId: number) => {
      return api.DeleteProductPricePricePointById(productPricePricePointId);
    },
    onSuccess: () => {
      // Invalidate the query to refresh the list of price points for the current price.
      queryClient.invalidateQueries({
        queryKey: ["productPricePricePoints", args.price.Id],
      });
    }
  });

  const findPricePoint = (pricePointId: number) => {
    const direct = queryClient.getQueryData<any>(["pricepointsById", pricePointId]);
    if (direct) {
      return direct;
    }
    const pages = queryClient.getQueriesData<any>({ queryKey: ["pricepoints"] });
    for (const [, data] of pages) {
      const match = data?.Results?.find((pp: any) => pp.Id === pricePointId);
      if (match) {
        return match;
      }
    }
    return null;
  };

  if (pricePointsQuery.isLoading || isProductPricePointsLoading) {
    return null;
  }
  return (
    <div className="space-y-2">
      {/* // TODO: Some kind of informational message here */}
      {/* <Surface variant="info">
        <FontAwesomeIcon icon={faQuestionCircle} />
        <span>{t("ui.productpricepricepoint.information", "Some default message")}</span>
      </Surface> */}
      {/* if there is a ProductPricePricePoint, the dropdown should no longer be rendered */}
      {productPricePricePoints!.Results.length === 0 &&
        <PagedResultsTypeahead
          label="Price Point"
          displayProperty="Name"
          paging={{ PageIndex: 0, PageSize: 20 }}
          onReload={async ({ Query = "", PageIndex = 0, PageSize = 20 }) => {
            const filters = { Query, PageIndex, PageSize };
            setPricePointFilters(filters);
            const requestKey = ["pricepoints", Query, PageIndex, PageSize];
            return queryClient.fetchQuery({
              queryKey: requestKey,
              queryFn: async () => {
                return api.ListPricePoint(filters);
              },
            });
          }}
          onSelect={(item) => {
            if (!item) {
              return;
            }
            queryClient.setQueryData(["pricepointsById", item.Id], item);
            selectProductPricePricePoint.mutate(item.Id);
          }}
        />
      }
      {productPricePricePoints!.Results.map((productPricePricePoint) => {
        const pricePoint = findPricePoint(productPricePricePoint.PricePointId);
        if (!pricePoint) {
          return null;
        }
        return (
          <Surface
            key={productPricePricePoint.Id}
            variant="outline"
            className="space-y-2">
            <div className="flex justify-between items-center p-2">
              <H2 className="border-0">{pricePoint.Name}</H2>
              {/* // TODO: something informational here? */}
              <Button
                type="button"
                variant="ghost"
                className="text-red-500 hover:text-red-700"
                onClick={() => deleteProductPricePricePoint.mutate(productPricePricePoint.Id)}
              >
                <span className="sr-only">{t("ui.remove", "Remove")}</span>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
            <hr />
            <ProductPricePricePointMinMaxForm productPricePricePoint={productPricePricePoint} />
          </Surface>
        );
      })}
    </div>
  );
}

interface ProductPricePricePointMinMaxFormProps {
  productPricePricePoint: ProductPricePricePoint;
}
const ProductPricePricePointMinMaxForm = ({ productPricePricePoint }: ProductPricePricePointMinMaxFormProps) => {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const form = useForm<ProductPricePricePoint>({
    defaultValues: productPricePricePoint,
  });

  const saveProductPricePricePoint = useMutation({
    mutationFn: async (data: ProductPricePricePoint) => {
      return api.UpdateProductPricePricePoint(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productPricePricePoints", productPricePricePoint.ProductPriceId],
      });
    }
  });

  const onSubmit = (data: ProductPricePricePoint) => {
    saveProductPricePricePoint.mutate(data, {
      onSuccess: () => {
        form.reset(data);
      },
      onError: (error) => {
        console.error("Error saving ProductPricePricePoint:", error);
      },
    });
  };

  return (
    <Form<ProductPricePricePoint> control={form.control}>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <LabeledInput
          label={(
            <TooltipWrapper
              tooltipContent={t("ui.productpricepricepoint.minQuantityTooltip", "Minimum quantity for this price point.")}
            >
              <div className="space-x-1">
                <FontAwesomeIcon icon={faQuestionCircle} />
                <span>{t("ui.minQuantity", "Min Quantity")}</span>
              </div>
            </TooltipWrapper>
          )}
          type="number"
          {...form.register("MinQuantity", {
            valueAsNumber: true,
            required: "Min Quantity is required",
          })}
        />
        <LabeledInput
          label={(
            <TooltipWrapper
              tooltipContent={t("ui.productpricepricepoint.maxQuantityTooltip", "Maximum quantity for this price point.")}
            >
              <div className="space-x-1">
                <FontAwesomeIcon icon={faQuestionCircle} />
                <span>{t("ui.maxQuantity", "Max Quantity")}</span>
              </div>
            </TooltipWrapper>
          )}
          type="number"
          {...form.register("MaxQuantity", {
            valueAsNumber: true,
            required: "Max Quantity is required",
          })}
        />
      </div>
      <Button type="button" variant="primary" onClick={() => form.handleSubmit(onSubmit)()}>
        {t("ui.save", "Save")}
      </Button>
    </Form>
  );
}

registerExtension("admin.inventory.products.pricing.price", AdminProductPricePricePointDropdown, "AdminProductPricePricePointDropdown");
